import { useState, useEffect, useRef } from 'react';
import { trpc } from '@/api/trpc';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { useAppQuery } from '@/hooks/useAppQuery';
import type {
    BarOHLCVDetailed,
    IndicatorInput,
    KeltnerChannelIndicator,
    RelativePerformanceLookbackPeriod
} from '@einhaus/project314-types';
import type { ChartSettings } from '@/components/Chart/ChartContainer.tsx';

type SupportedIndicatorInput = Exclude<IndicatorInput, KeltnerChannelIndicator>;

const isSupportedIndicatorInput = (indicator: IndicatorInput): indicator is SupportedIndicatorInput => indicator.type !== 'keltner';

export const useTrpcChartData = (chartSettings: ChartSettings) => {
    const [error, setError] = useState<Error | null>(null);
    const [comparisonError, setComparisonError] = useState<Error | null>(null);

    // Use a ref to track previous chartSettings for comparison
    const prevChartSettingsRef = useRef<ChartSettings | null>(null);

    // Create query options with the chartSettingsKey to force refetch when settings change
    const barsQueryOptions = !chartSettings.option
        ? trpc.entity.fetchBarsForDuration.queryOptions({
              entityId: chartSettings.entity.id,
              entityType: chartSettings.entity.entityType,
              startDateTime: chartSettings.startDateTime,
              endDateTime: chartSettings.endDateTime,
              duration: chartSettings.duration,
              shouldRemoveAfterHours: chartSettings.shouldRemoveAfterHours,
              shouldIncludeCandlestickPatterns: chartSettings.shouldIncludeCandlestickPatterns,
              indicators: chartSettings.indicatorsWithChartSettings.map((wrapper) => wrapper.indicator).filter(isSupportedIndicatorInput)
          })
        : trpc.bars.fetchOptionBars.queryOptions({
              entityId: chartSettings.entity.id,
              entityType: chartSettings.entity.entityType,
              optionId: chartSettings.option.id,
              startDateTime: chartSettings.startDateTime,
              endDateTime: chartSettings.endDateTime,
              duration: chartSettings.duration
          });

    // Fetch bars data based on whether we're looking at an option or regular entity
    const barsQuery = useAppQuery({
        ...barsQueryOptions,
        // Force refetch when chartSettingsKey changes
        refetchOnMount: true,
        refetchInterval: chartSettings.shouldAutoRefreshBars ? 30000 : false // 30 seconds
    });

    // Create comparison query options
    const comparisonQueryOptions = trpc.technicalAnalysis.compareEntities.queryOptions({
        primaryEntityId: chartSettings.entity.id,
        entityType: chartSettings.entity.entityType,
        comparisonEntityId: chartSettings.comparisonEntity?.id || 0,
        comparisonEntityType: chartSettings.comparisonEntity?.type || 'Asset',
        duration: chartSettings.duration,
        startDateTime: chartSettings.startDateTime,
        endDateTime: chartSettings.endDateTime,
        shouldRemoveAfterHours: chartSettings.shouldRemoveAfterHours,
        anchoredLookbackPeriods: ['7', '14', '30', '60', '90'] as RelativePerformanceLookbackPeriod[]
    });

    // Fetch comparison data if a comparison entity is selected
    const comparisonQuery = useAppQuery({
        ...comparisonQueryOptions,
        // Force refetch when chartSettingsKey changes
        refetchOnMount: true,
        enabled: !!chartSettings.comparisonEntity,
        refetchInterval: chartSettings.shouldAutoRefreshBars && !!chartSettings.comparisonEntity ? 30000 : false
    });

    // Handle errors for bars query
    useEffect(() => {
        if (barsQuery.error) {
            handleTrpcQuery(barsQuery, {
                fallbackErrorMessage: 'Failed to load chart data',
                showErrorToast: true,
                setError: (msg) => setError(new Error(msg))
            });
        } else {
            setError(null);
        }
        // Only depend on the error property, not the entire query object
    }, [barsQuery.error]);

    // Handle errors for comparison query
    useEffect(() => {
        if (comparisonQuery.error) {
            handleTrpcQuery(comparisonQuery, {
                fallbackErrorMessage: 'Failed to load comparison data',
                showErrorToast: true,
                setError: (msg) => setComparisonError(new Error(msg))
            });
        } else if (!chartSettings.comparisonEntity) {
            setComparisonError(null);
        }
        // Only depend on the error property and comparisonEntity, not the entire query object
    }, [comparisonQuery.error, chartSettings.comparisonEntity]);

    // Refetch data when chartSettings change
    useEffect(() => {
        // Check if any relevant settings have changed
        const shouldRefetch = prevChartSettingsRef.current?.key !== chartSettings.key;

        // Only refetch if something relevant has changed
        if (shouldRefetch) {
            // Refetch bars data
            void barsQuery.refetch();

            // Refetch comparison data if enabled
            if (chartSettings.comparisonEntity) {
                void comparisonQuery.refetch();
            }
        }

        // Update the ref with current settings
        prevChartSettingsRef.current = { ...chartSettings };
    }, [chartSettings.key]);

    // Extract and process the data
    const bars = barsQuery.data?.bars || [];
    const comparisonData = comparisonQuery.data?.data || null;
    const isLoading = barsQuery.isLoading || (!!chartSettings.comparisonEntity && comparisonQuery.isLoading);

    return {
        bars: bars as BarOHLCVDetailed[],
        isLoading,
        error,
        comparisonData,
        comparisonError,
        refetch: barsQuery.refetch,
        refetchComparison: comparisonQuery.refetch
    };
};

export default useTrpcChartData;
